import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Search, Film, Camera, Music, Palette, Zap, Briefcase, Users, Lightbulb, Edit, Scissors, FileText, Mic } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const DepartmentsListPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const departments = [
    {
      id: 'art-department',
      name: 'Art Department',
      icon: Palette,
      description: 'Set designers, art directors, prop masters, and visual stylists',
      count: 4200,
      color: 'from-orange-500/10 to-red-500/10'
    },
    {
      id: 'camera-department',
      name: 'Camera Department',
      icon: Camera,
      description: 'Cinematographers, camera operators, DPs, and focus pullers',
      count: 6800,
      color: 'from-blue-500/10 to-cyan-500/10'
    },
    {
      id: 'sound-department',
      name: 'Sound Department',
      icon: Music,
      description: 'Sound engineers, boom operators, and audio mixers',
      count: 3100,
      color: 'from-purple-500/10 to-pink-500/10'
    },
    {
      id: 'direction',
      name: 'Direction',
      icon: Film,
      description: 'Directors, creative heads, and vision leaders',
      count: 2400,
      color: 'from-green-500/10 to-emerald-500/10'
    },
    {
      id: 'makeup-hair',
      name: 'Makeup & Hair',
      icon: Palette,
      description: 'Makeup artists, hair stylists, and prosthetics experts',
      count: 3600,
      color: 'from-pink-500/10 to-rose-500/10'
    },
    {
      id: 'sfx-vfx',
      name: 'SFX / VFX',
      icon: Zap,
      description: 'Visual effects artists, 3D modelers, and compositors',
      count: 5200,
      color: 'from-indigo-500/10 to-violet-500/10'
    },
    {
      id: 'production',
      name: 'Production',
      icon: Briefcase,
      description: 'Line producers, production managers, and coordinators',
      count: 4800,
      color: 'from-yellow-500/10 to-amber-500/10'
    },
    {
      id: 'assistant-directors',
      name: 'Assistant Directors',
      icon: Users,
      description: '1st AD, 2nd AD, and floor managers',
      count: 3900,
      color: 'from-teal-500/10 to-cyan-500/10'
    },
    {
      id: 'lighting-department',
      name: 'Lighting Department',
      icon: Lightbulb,
      description: 'Gaffers, lighting technicians, and electricians',
      count: 2800,
      color: 'from-orange-500/10 to-yellow-500/10'
    },
    {
      id: 'post-production',
      name: 'Post Production',
      icon: Edit,
      description: 'Editors, colorists, and post-production supervisors',
      count: 6100,
      color: 'from-blue-500/10 to-indigo-500/10'
    },
    {
      id: 'script-department',
      name: 'Script Department',
      icon: FileText,
      description: 'Scriptwriters, continuity supervisors, and script coordinators',
      count: 2100,
      color: 'from-purple-500/10 to-blue-500/10'
    },
    {
      id: 'editing',
      name: 'Editing',
      icon: Scissors,
      description: 'Video editors, assistant editors, and online editors',
      count: 5500,
      color: 'from-green-500/10 to-teal-500/10'
    }
  ];

  const filteredDepartments = departments.filter(dept =>
    dept.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    dept.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleDepartmentClick = (deptId) => {
    navigate(`/department/${deptId}`);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b border-border backdrop-blur-sm bg-background/95 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/')}
                className="hover:bg-accent/10 hover:text-accent"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity bg-transparent border-none cursor-pointer"
              >
                <img src="/logo.png" alt="Shoot Base" className="h-14 md:h-20 w-auto object-contain" />
                <h1 className="text-xl md:text-2xl font-bold text-accent" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Shoot Base
                </h1>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-b from-orange-ultralight to-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto mb-8"
          >
            <Badge className="mb-4 bg-accent/10 text-accent border-accent/30">
              Browse Departments
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">
              Find Professionals by
              <span className="text-accent"> Department</span>
            </h1>
            <p className="text-lg text-muted-foreground mb-6">
              Explore 12+ specialized departments and connect with verified film crew across India
            </p>
          </motion.div>

          {/* Search Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="max-w-2xl mx-auto"
          >
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
              <Input
                type="text"
                placeholder="Search departments..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 py-6 text-base bg-card border-border"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Departments Grid */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredDepartments.map((dept, index) => (
              <motion.div
                key={dept.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card
                  className={`h-full bg-gradient-to-br ${dept.color} border-border hover:border-accent/50 transition-all cursor-pointer hover:shadow-lg group`}
                  onClick={() => handleDepartmentClick(dept.id)}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="bg-accent/10 w-12 h-12 rounded-xl flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                        <dept.icon className="w-6 h-6 text-accent" />
                      </div>
                      <Badge className="bg-accent/10 text-accent border-accent/30 text-xs">
                        {dept.count.toLocaleString()}+ professionals
                      </Badge>
                    </div>
                    <h3 className="text-xl font-semibold mb-2 text-foreground group-hover:text-accent transition-colors">
                      {dept.name}
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {dept.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {filteredDepartments.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground text-lg">No departments found matching "{searchQuery}"</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-b from-orange-ultralight to-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto"
          >
            <h2 className="text-3xl font-bold mb-4 text-foreground">Can't Find What You're Looking For?</h2>
            <p className="text-muted-foreground mb-6">
              Post a hiring request and let professionals come to you
            </p>
            <Button 
              size="lg" 
              className="bg-accent hover:bg-accent/90 text-primary-foreground font-semibold"
              onClick={() => navigate('/post-project')}
            >
              Create a Hiring Post
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default DepartmentsListPage;